import { useState } from "react";
import { Ban } from "lucide-react";
import { setCompanySkipStatus, type PrimeCompany } from "../api/CrmApi";
import { COMPANY_STATUS_LABELS, type CompanySkipStatus } from "../companyStatus";
import { apiErrorMessage } from "../apiError";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  companyId: number;
  companyName: string;
  status: CompanySkipStatus;
  onClose: () => void;
  onSaved: (company: PrimeCompany) => void;
}

const MEANING: Record<CompanySkipStatus, string> = {
  not_a_fit: "This company does no software-related work at all. It stays in the CRM with its contacts and research, and is skipped for every future RFP.",
  inactive: "This company no longer exists (dissolved or inactive). It stays in the CRM with its contacts and research, and is skipped for every future RFP.",
};

/** Mark a company Not a fit or Inactive. Needs a reason; the server refuses while a campaign still locks the company. */
export default function CompanySkipStatusModal({ companyId, companyName, status, onClose, onSaved }: Props) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = COMPANY_STATUS_LABELS[status];
  const missingReason = reason.trim().length === 0;

  async function confirm() {
    if (missingReason) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await setCompanySkipStatus(companyId, status, reason.trim()));
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not save the status. Please try again."));
      setSaving(false);
    }
  }

  return (
    <Modal
      size="md"
      title={`Mark ${label}`}
      description={companyName}
      icon={<Ban />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={confirm} disabled={saving || missingReason} title={missingReason ? "Add a reason first" : undefined}>
            {saving ? "Saving…" : `Mark ${label}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-foreground">{MEANING[status]}</p>
        <p className="text-xs text-muted-foreground">On RFPs where its role is still unconfirmed it is taken off the list (unless a contact there has replied or carries your notes). Confirmed primes and past RFPs are not changed. You can re-open it at any time.</p>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5" htmlFor="company-skip-reason">Reason</label>
          <textarea
            id="company-skip-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={status === "not_a_fit" ? "e.g. Civil construction only, no software work found on its site or in its past projects." : "e.g. Dissolved in 2025 (state registry), website is gone."}
            className={`w-full px-3 py-2 ${FORM_CONTROL} resize-none`}
          />
        </div>
        {error && <Alert tone="danger" role="alert">{error}</Alert>}
      </div>
    </Modal>
  );
}
